import React, { useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { CONTACT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MessageCircle, Loader2 } from "lucide-react";

export default function InquiryForm({ variant = "card" }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: "", treatment: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/inquiries`, { ...form, inquiry_type: "consultation" });
      toast.success(r.data.message || t("inquiry.success"));
      setForm({ name: "", email: "", phone: "", country: "", treatment: "", message: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : t("inquiry.error"));
    } finally {
      setLoading(false);
    }
  };

  const wrapCls = variant === "plain"
    ? ""
    : "bg-white rounded-3xl border border-stone-200 shadow-sm p-6 sm:p-10";

  return (
    <form onSubmit={submit} className={wrapCls} data-testid="inquiry-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="inq-name" className="text-sm">{t("inquiry.name")}</Label>
          <Input id="inq-name" required value={form.name} onChange={upd("name")} data-testid="inquiry-name" className="mt-1.5 rounded-xl bg-white" />
        </div>
        <div>
          <Label htmlFor="inq-email" className="text-sm">{t("inquiry.email")}</Label>
          <Input id="inq-email" type="email" required value={form.email} onChange={upd("email")} data-testid="inquiry-email" className="mt-1.5 rounded-xl bg-white" />
        </div>
        <div>
          <Label htmlFor="inq-phone" className="text-sm">{t("inquiry.phone")}</Label>
          <Input id="inq-phone" required value={form.phone} onChange={upd("phone")} data-testid="inquiry-phone" className="mt-1.5 rounded-xl bg-white" />
        </div>
        <div>
          <Label htmlFor="inq-country" className="text-sm">{t("inquiry.country")}</Label>
          <Input id="inq-country" value={form.country} onChange={upd("country")} data-testid="inquiry-country" className="mt-1.5 rounded-xl bg-white" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="inq-treatment" className="text-sm">{t("inquiry.treatment")}</Label>
          <Input id="inq-treatment" value={form.treatment} onChange={upd("treatment")} data-testid="inquiry-treatment" className="mt-1.5 rounded-xl bg-white" />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="inq-message" className="text-sm">{t("inquiry.message")}</Label>
          <Textarea id="inq-message" rows={4} value={form.message} onChange={upd("message")} data-testid="inquiry-message" className="mt-1.5 rounded-xl bg-white" />
        </div>
      </div>
      <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        <Button type="submit" disabled={loading} data-testid="inquiry-submit" className="rounded-full bg-ochre-500 hover:bg-ochre-600 text-white h-12 px-8">
          {loading ? <><Loader2 className="me-2 h-4 w-4 animate-spin" />{t("inquiry.submitting")}</> : t("inquiry.submit")}
        </Button>
        <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" data-testid="inquiry-whatsapp" className="inline-flex items-center gap-2 text-sm text-sage-900 hover:text-ochre-500 font-medium">
          <MessageCircle className="h-4 w-4" /> {t("inquiry.whatsapp")}
        </a>
      </div>
    </form>
  );
}
